import {Children} from 'react';

import styled from '@emotion/styled';
import {translucify} from '@nfq/colors';
import {media, Spacer, spacing} from '@nfq/react-grid';
import AutoScroll from 'embla-carousel-auto-scroll';
import Autoplay from 'embla-carousel-autoplay';
import {observer} from 'mobx-react-lite';

import {useSlider} from './useSlider';
import {ArrowRight} from 'Images/icons/ArrowRight';

import type {EmblaOptionsType} from 'embla-carousel';
import type {WithChildren} from 'types/global';

/**
 * The `ComponentProps` interface defines the configuration and customization options
 * for the slider component. It allows developers to control playback behavior, styling,
 * navigation visibility, and other rendering aspects. This interface is commonly used
 * with Embla-based sliders to define how they should behave and appear.
 */
interface ComponentProps {
    /**
     * Enables or disables autoplay for the slider.
     * When set to `true`, the slider will automatically transition between slides at set intervals.
     */
    autoplay?: boolean;
    /**
     * The interval in milliseconds used for autoplay transitions.
     * This determines how long each slide is displayed before moving to the next.
     */
    autoplayInterval?: number;
    /**
     * Enables continuous autoscroll instead of step-based autoplay.
     * When `true`, the slider will scroll at a steady speed rather than jumping between slides.
     */
    autoscroll?: boolean;
    /**
     * A string representing the CSS class to apply to the root slider component or its controls.
     * Useful for overriding default styles or applying theme-specific modifications.
     */
    className?: string;
    /**
     * A boolean that indicates whether the navigation arrows should be hidden.
     * If `true`, the "previous" and "next" buttons will not be rendered.
     */
    hideArrows?: boolean;
    /**
     * A boolean that determines if overflow content in the slider should be hidden.
     * When `true`, any slide content extending beyond the container's bounds will be clipped.
     */
    overflowHidden?: boolean;
    /**
     * A callback function for updating the active slide index in the parent component.
     * This allows external control over the slider's currently displayed slide.
     *
     * @param newIndex The new slide index to be set.
     */
    setActiveSlideIndex?(newIndex: number): void;
    /**
     * Configuration object passed to the Embla Carousel instance to customize slider behavior.
     * Includes properties like `loop`, `align`, and `slidesToScroll` among others.
     */
    sliderOptions?: EmblaOptionsType;
    /**
     * A unique identifier used for testing the component.
     * Typically used with `data-cy` or `data-testid` attributes in test frameworks.
     */
    testId?: string;
    /**
     * The layout variant of the slider, used to control structural rendering logic.
     * Options include `'general'` for default layout and `'usp'` for a custom format.
     */
    variant?: 'general' | 'usp';
}

/**
 * Renders a responsive and highly customizable slider component using the Embla Carousel library.
 * This component supports features like autoplay, autoscroll, navigation controls, responsive layout,
 * and conditional rendering of arrow buttons. It is capable of rendering any set of React nodes as slides.
 *
 * It leverages the `useSlider` hook for internal state and navigation management and adapts its behavior
 * based on the provided props. The slider supports two layout variants: `'general'` and `'usp'`, each having
 * a distinct visual structure.
 *
 * @param props                     The component props.
 * @param props.autoplay            Enables autoplay of the slider if set to `true`.
 * @param props.autoplayInterval    The delay in milliseconds between autoplay slide transitions.
 * @param props.autoscroll          Enables continuous scrolling if set to `true`.
 * @param props.children            The slides to render inside the slider, passed as React children.
 * @param props.className           An optional CSS class applied to the root slider wrapper.
 * @param props.hideArrows          Hides the navigation arrows when set to `true`.
 * @param props.overflowHidden      Controls whether to visually hide overflow content.
 * @param props.setActiveSlideIndex Optional callback function to update the active slide index.
 * @param props.sliderOptions       Optional Embla configuration to override default slider behavior.
 * @param props.testId              A unique string identifier for the component used in testing (e.g., `data-cy`).
 * @param props.variant             Determines which visual layout to render. Accepts `'general'` or `'usp'`. Defaults to `'general'`.
 *
 * @returns A React slider component with optional autoplay, scrolling, and theming options.
 *
 * @example
 * ```tsx
 * <Slider testId="product-slider" autoplay autoplayInterval={5000}>
 *   <ProductCard />
 *   <ProductCard />
 *   <ProductCard />
 * </Slider>
 * ```
 */
const Slider = observer(({
    autoplay = false,
    autoplayInterval,
    autoscroll = false,
    children,
    className = '',
    hideArrows,
    overflowHidden = false,
    setActiveSlideIndex,
    sliderOptions,
    testId = 'Slider',
    variant = 'general'
}: WithChildren<ComponentProps>) => {
    const {
        iconSize,
        nextBtnDisabled,
        onNextButtonClick,
        onPrevButtonClick,
        prevBtnDisabled,
        slideRef
    } = useSlider(
        {
            align: 'start',
            loop: true,
            slidesToScroll: 1,
            ...sliderOptions
        },
        [
            ...(autoplay ? [
                Autoplay({
                    delay: autoplayInterval,
                    playOnInit: true,
                    stopOnFocusIn: true,
                    stopOnInteraction: true,
                    stopOnMouseEnter: true
                })
            ] : []),
            ...(autoscroll ? [AutoScroll({
                playOnInit: true,
                speed: 1,
                startDelay: 0,
                stopOnFocusIn: false,
                stopOnInteraction: false,
                stopOnMouseEnter: false
            })] : [])
        ],
        setActiveSlideIndex
    );

    return (
        <SliderWrapper className={className} data-cy={testId}>
            {variant === 'usp' && (
                <>
                    <Viewport ref={slideRef} $overflowHidden={overflowHidden}>
                        <ContainerUSP>
                            {Children.map(children, (child, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <SlideUSP key={index}>{child}</SlideUSP>
                            ))}
                        </ContainerUSP>
                    </Viewport>
                    {(!nextBtnDisabled || !prevBtnDisabled) && !hideArrows && (
                        <>
                            <Spacer y={12} />
                            <Controls>
                                <ArrowButton
                                    $direction="prev"
                                    aria-label="Previous"
                                    disabled={prevBtnDisabled}
                                    onClick={onPrevButtonClick}
                                >
                                    <ArrowRight color1="currentColor" height={iconSize} width={iconSize} />
                                </ArrowButton>
                                <ArrowButton
                                    $direction="next"
                                    aria-label="Next"
                                    disabled={nextBtnDisabled}
                                    onClick={onNextButtonClick}
                                >
                                    <ArrowRight color1="currentColor" height={iconSize} width={iconSize} />
                                </ArrowButton>
                            </Controls>
                        </>
                    )}
                </>
            )}
            {variant === 'general' && (
                <>
                    {(!nextBtnDisabled || !prevBtnDisabled) && !hideArrows && (
                        <>
                            <Controls>
                                <ArrowButton
                                    $direction="prev"
                                    aria-label="Previous"
                                    disabled={prevBtnDisabled}
                                    onClick={onPrevButtonClick}
                                >
                                    <ArrowRight color1="currentColor" height={iconSize} width={iconSize} />
                                </ArrowButton>
                                <ArrowButton
                                    $direction="next"
                                    aria-label="Next"
                                    disabled={nextBtnDisabled}
                                    onClick={onNextButtonClick}
                                >
                                    <ArrowRight color1="currentColor" height={iconSize} width={iconSize} />
                                </ArrowButton>
                            </Controls>
                            <Spacer y={5} />
                        </>
                    )}
                    <Viewport ref={slideRef} $overflowHidden={overflowHidden}>
                        <Container>
                            {Children.map(children, (child, index) => (
                                // eslint-disable-next-line react/no-array-index-key
                                <Slide key={index}>
                                    {child}
                                </Slide>
                            ))}
                        </Container>
                    </Viewport>
                </>
            )}
        </SliderWrapper>
    );
});

Slider.displayName = 'Slider';

export {Slider};

const SliderWrapper = styled.div`
    position: relative;
    width: 100%;
`;

interface ViewportProps {
    $overflowHidden?: boolean;
}

const Viewport = styled.div<ViewportProps>`
    overflow: ${({$overflowHidden}) => ($overflowHidden ? 'hidden' : 'visible')};
`;

const ContainerUSP = styled.div`
    backface-visibility: hidden;
    display: flex;
    touch-action: pan-y;
`;

const SlideUSP = styled.div`
    flex: 0 0 auto;
    width: 100%;
`;

const Controls = styled.div`
    align-items: center;
    display: flex;
    gap: ${spacing(5.5)};
    justify-content: flex-end;
`;

interface ArrowButtonProps {
    $direction?: 'next' | 'prev';
}

const ArrowButton = styled.button<ArrowButtonProps>`
    background: none;
    border: none;
    color: ${({theme}) => theme.colors.primary};
    cursor: pointer;
    outline: none;
    transform: ${({$direction}) => ($direction === 'prev' ? 'scaleX(-1)' : null)};
    transition: opacity 0.2s ease-in-out;
    will-change: opacity color;

    &:hover:not(:disabled) {
        opacity: 0.8;
    }

    &:disabled {
        background: none;
        color: ${({theme}) => translucify(theme.colors.acePrimary, 40)};
        cursor: not-allowed;
    }
`;

const Container = styled(ContainerUSP)`
    margin-inline: -1.5rem;

    ${media('md')} {
        margin-inline: -4rem;
    }
`;

const Slide = styled.div`
    margin-inline: 1.5rem;
    min-width: 15rem;
    width: 15rem;

    ${media('md')} {
        margin-inline: 4rem;
        min-width: 30rem;
        width: 30rem;
    }
`;